/**
 * ant-design-vue v1 的 <a-icon type="xxx" /> 兼容层
 *
 * antd v4 已删除 Icon 组件，图标全部移到 @ant-design/icons-vue 的独立组件。
 * 项目里 27 处 <a-icon> 在升级后会静默不渲染（控制台只有
 * "Failed to resolve component: a-icon"），这里把旧写法注册成同名全局组件，
 * 按 type(+theme) 映射到对应的 SVG 图标组件。
 */
import { h } from 'vue'
import {
    CheckOutlined,
    CloseOutlined,
    CloseCircleOutlined,
    CloseCircleFilled,
    SettingOutlined,
    RightOutlined,
    PlusOutlined,
    UserOutlined,
    LogoutOutlined,
    LoadingOutlined,
    BellOutlined,
    SearchOutlined,
    CopyOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    EnvironmentOutlined,
} from '@ant-design/icons-vue'

const iconMap = {
    'check': CheckOutlined,
    'close': CloseOutlined,
    'close-circle': CloseCircleOutlined,
    'close-circle:filled': CloseCircleFilled,
    'setting': SettingOutlined,
    'right': RightOutlined,
    'plus': PlusOutlined,
    'user': UserOutlined,
    'logout': LogoutOutlined,
    'loading': LoadingOutlined,
    'bell': BellOutlined,
    'search': SearchOutlined,
    'copy': CopyOutlined,
    'cloud-upload': CloudUploadOutlined,
    'delete': DeleteOutlined,
    'environment': EnvironmentOutlined,
}

const AIcon = {
    name: 'AIcon',
    props: {
        type: { type: String, default: '' },
        theme: { type: String, default: '' },
        spin: { type: Boolean, default: false },
        // antdv1 的 <a-icon :component="xxx"/> 用法：本项目传的是
        // svg?inline 产出的 data URL 字符串（也可能是组件/render 函数），
        // 缺这个支持时店铺装修组件面板、手机预览、客户端图标全都静默不渲染
        component: { type: [String, Object, Function], default: null },
    },
    setup(props, { attrs }) {
        return () => {
            if (props.component) {
                // 组件 / render 函数：直接当图标组件渲染
                if (typeof props.component !== 'string') {
                    return h(props.component, Object.assign({}, attrs, { spin: props.spin }))
                }
                // 字符串：data URL / svg 源码 → 内联渲染；图片 URL → <img>
                let src = props.component
                let svg = null
                const m = /^data:image\/svg\+xml[^,]*,(.+)$/i.exec(src)
                if (m) {
                    try { svg = decodeURIComponent(m[1]) } catch (e) { /* 解码失败按普通图片处理 */ }
                } else if (/^\s*<svg[\s>]/i.test(src)) {
                    svg = src
                }
                if (svg && /<svg[\s>]/i.test(svg)) {
                    // 去掉 svg 自带 width/height，尺寸跟随 font-size；fill 继承文字颜色
                    svg = svg.replace(/\s(width|height)="[^"]*"/gi, '')
                    return h('span', Object.assign({}, attrs, {
                        class: ['anticon', attrs.class],
                        style: 'display:inline-flex;align-items:center;justify-content:center;line-height:0;width:1em;height:1em',
                        innerHTML: svg.replace(/<svg/i, '<svg style="width:100%;height:100%;fill:currentColor"'),
                    }))
                }
                // 普通 URL（本项目 svg?inline 在 webpack5 下产出 img/xxx.svg）：用 img 展示
                return h('span', Object.assign({}, attrs, {
                    class: ['anticon', attrs.class],
                    style: 'display:inline-flex;align-items:center;justify-content:center;line-height:0;width:1em;height:1em',
                }), [
                    h('img', { src: src, style: 'width:100%;height:100%;display:block;object-fit:contain' }),
                ])
            }
            const Icon = (props.theme && iconMap[`${props.type}:${props.theme}`]) || iconMap[props.type]
            if (!Icon) return null
            return h(Icon, Object.assign({}, attrs, {
                spin: props.spin || props.type === 'loading',
            }))
        }
    },
}

export default {
    install(app) {
        app.component('AIcon', AIcon)
    },
}
